// src/services/notifications.ts
import { db, auth } from '../lib/firebase';
import type { Notification, NotificationLink } from '../types';
import { NotificationType, View } from '../types';

import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  writeBatch,
  where,
  setDoc,
  increment,
} from 'firebase/firestore';

function notifCol(uid: string) {
  return collection(db, 'users', uid, 'notifications');
}

function userRef(uid: string) {
  return doc(db, 'users', uid);
}

function toMillisMaybe(v: any): number | null {
  if (!v) return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (v instanceof Date) return v.getTime();
  if (v instanceof Timestamp) return v.toMillis();
  if (typeof v?.toMillis === 'function') return v.toMillis();
  if (typeof v?.seconds === 'number') return Math.floor(v.seconds * 1000);
  return null;
}

function safeStr(v: any): string | undefined {
  const s = typeof v === 'string' ? v.trim() : '';
  return s ? s : undefined;
}

function isCrewNotificationType(type: any): boolean {
  const upper = String(type || '').toUpperCase();
  return (
    type === NotificationType.NEW_FOLLOW ||
    upper.includes('FRIEND') ||
    upper.includes('CREW') ||
    upper.includes('FOLLOW')
  );
}

function isCrewMessageNotificationType(type: any): boolean {
  return String(type || '').toUpperCase() === 'CREW_MESSAGE';
}

function isCrewActivityNotificationType(type: any): boolean {
  const upper = String(type || '').toUpperCase();
  return upper === 'CREW_POST' || upper === 'CREW_REPLY' || upper.includes('CREW_ACTIVITY');
}

function normalizeViewLike(viewLike: any): View | undefined {
  if (!viewLike) return undefined;

  const values = Object.values(View) as any[];
  if (values.includes(viewLike)) return viewLike as View;

  const s = String(viewLike).trim();
  if (!s) return undefined;

  const key = s.includes('.') ? s.split('.').pop()! : s;
  const k = key.toLowerCase();

  if (k === 'community' || k === 'social' || k === 'baleenbay') return View.Community;
  if (k === 'receipt' || k === 'transaction' || k === 'tx') return (View as any).Receipt ?? View.Notifications;
  if (k === 'catchdiary' || k === 'diary' || k === 'log') return (View as any).CatchDiary ?? View.Map;
  if (k === 'bazaar' || k === 'marketplace') return (View as any).Bazaar ?? View.Map;
  if (k === 'notifications') return View.Notifications;
  if (k === 'events' || k === 'tournament') return View.Events;
  if (k === 'licensedetail' || k === 'license') return View.LicenseDetail;
  if (k === 'profile') return View.Profile;
  if (k === 'publicprofile' || k === 'public_profile' || k === 'angler') return View.PublicProfile;

  const direct = (View as any)[key];
  if (direct) return direct as View;

  return undefined;
}

function normalizeLinkMaybe(raw: any): NotificationLink | undefined {
  if (!raw) return undefined;

  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return undefined;

    try {
      const obj = JSON.parse(s);
      return normalizeLinkMaybe(obj);
    } catch {}

    try {
      const obj = JSON.parse(s.replace(/'/g, '"'));
      return normalizeLinkMaybe(obj);
    } catch {}

    const mComment = s.match(/\/post\/([^/?#]+).*?(?:commentId=|comment=|#comment=)([^&#]+)/i);
    if (mComment?.[1]) {
      return { view: View.Community, targetId: mComment[1], subTargetId: mComment?.[2] };
    }

    const mPost = s.match(/\/post\/([^/?#]+)/i) || s.match(/community[:/ ]+post[:/ ]+([^/?#]+)/i);
    if (mPost?.[1]) return { view: View.Community, targetId: mPost[1] };

    const mReceipt =
      s.match(/receipt[:/ ]+([^/?#]+)/i) ||
      s.match(/tx[:/ ]+([^/?#]+)/i) ||
      s.match(/session[:/ ]+([^/?#]+)/i);
    if (mReceipt?.[1]) {
      return { view: (View as any).Receipt ?? View.Notifications, targetId: mReceipt[1] };
    }

    return undefined;
  }

  if (typeof raw === 'object' && raw.view) {
    const view = normalizeViewLike(raw.view);
    const targetId = raw.targetId != null ? String(raw.targetId) : '';
    const subTargetId = raw.subTargetId != null ? String(raw.subTargetId) : undefined;
    if (targetId && view) return { view, targetId, ...(subTargetId ? { subTargetId } : {}) };
  }

  if (typeof raw === 'object') {
    const viewLike = raw.view ?? raw.screen ?? raw.route ?? raw.to;
    const targetLike = raw.targetId ?? raw.id ?? raw.postId ?? raw.itemId ?? raw.txId ?? raw.catchId ?? raw.licenseId;
    const subLike = raw.subTargetId ?? raw.commentId ?? raw.offerId;

    const view = normalizeViewLike(viewLike);
    if (view && targetLike != null) {
      const targetId = String(targetLike);
      const subTargetId = subLike != null ? String(subLike) : undefined;
      return { view, targetId, ...(subTargetId ? { subTargetId } : {}) };
    }
  }

  return undefined;
}


async function shouldCreateNotificationForRecipient(userId: string, type: any): Promise<boolean> {
  // Recipient settings live on private users/{uid}. A sender must never read
  // another angler's private profile just to decide whether a notification may
  // be created. Cross-user notification writes are already constrained by
  // Firestore rules; preference enforcement for those belongs server-side.
  if (auth.currentUser?.uid !== userId) return true;

  // Self-created/local notifications may honor the signed-in user's settings.
  try {
    const snap = await getDoc(userRef(userId));
    const settings = snap.exists() ? ((snap.data() as any)?.notificationSettings || {}) : {};
    if (settings?.enabled === false) return false;

    if (isCrewMessageNotificationType(type)) {
      return settings?.crewMessages !== false;
    }

    if (isCrewActivityNotificationType(type)) {
      return settings?.crewActivity !== false;
    }

    if (isCrewNotificationType(type)) {
      return settings?.crewRequests !== false;
    }

    switch (type) {
      case NotificationType.NEW_LIKE:
        return settings?.newLikes !== false;
      case NotificationType.NEW_COMMENT:
      case NotificationType.NEW_REPLY:
      case NotificationType.NEW_MENTION:
        return settings?.newComments !== false;
      case NotificationType.PRICE_DROP:
      case NotificationType.NEW_OFFER:
      case NotificationType.OFFER_ACCEPTED:
      case NotificationType.OFFER_DECLINED:
      case NotificationType.OFFER_COUNTERED:
      case NotificationType.PAYMENT_SUCCESS:
      case NotificationType.ESCROW_RELEASED:
        return settings?.priceDrops !== false;
      default:
        return true;
    }
  } catch (err) {
    console.warn('[notifications] settings check skipped', err);
    return true;
  }
}

function stripUndefinedDeep<T>(value: T): T {
  if (Array.isArray(value)) return value.map((v) => stripUndefinedDeep(v)).filter((v) => v !== undefined) as any;
  if (value && typeof value === 'object') {
    const out: Record<string, any> = {};
    Object.entries(value as Record<string, any>).forEach(([key, val]) => {
      if (val === undefined) return;
      out[key] = stripUndefinedDeep(val);
    });
    return out as T;
  }
  return value;
}

function inferLinkFromEntityOrType(data: any): NotificationLink | undefined {
  const entity = data?.entity ?? {};
  const type = data?.type;

  const postId = safeStr(entity?.postId ?? data?.postId);
  const commentId = safeStr(entity?.commentId ?? data?.commentId);
  const itemId = safeStr(entity?.itemId ?? data?.itemId);
  const offerId = safeStr(entity?.offerId ?? data?.offerId);
  const txId = safeStr(entity?.txId ?? data?.txId);
  const catchId = safeStr(entity?.catchId ?? data?.catchId);
  const licenseId = safeStr(entity?.licenseId ?? data?.licenseId);
  const tournamentId = safeStr(entity?.tournamentId ?? data?.tournamentId);
  const dmId = safeStr(entity?.dmId ?? data?.dmId);
  const dmPeerUid = safeStr(entity?.userId ?? data?.dmPeerUid ?? data?.subTargetId);
  const userId = safeStr(entity?.userId ?? data?.userId ?? data?.targetUserId ?? data?.profileUid);

  if (dmId && isCrewMessageNotificationType(type)) {
    return {
      view: View.Crew,
      targetId: dmId,
      ...(dmPeerUid ? { subTargetId: dmPeerUid } : {}),
    };
  }

  if (
    postId &&
    (type === NotificationType.NEW_LIKE ||
      type === NotificationType.NEW_COMMENT ||
      String(type || '').toUpperCase().includes('REPLY') ||
      String(type || '').toUpperCase().includes('COMMENT') ||
      String(type || '').toUpperCase().includes('LIKE'))
  ) {
    return {
      view: View.Community,
      targetId: postId,
      ...(commentId ? { subTargetId: commentId } : {}),
    };
  }

  if (userId && isCrewNotificationType(type)) {
    return { view: View.PublicProfile, targetId: userId };
  }

  if (txId) return { view: (View as any).Receipt ?? View.Notifications, targetId: txId };
  if (catchId) return { view: View.LogCatch, targetId: catchId };
  if (licenseId) return { view: View.LicenseDetail, targetId: licenseId };
  if (tournamentId) return { view: View.Events, targetId: tournamentId };

  if (itemId) {
    return {
      view: (View as any).Bazaar ?? View.Map,
      targetId: itemId,
      ...(offerId ? { subTargetId: offerId } : {}),
    };
  }

  if (offerId) return { view: (View as any).Bazaar ?? View.Map, targetId: offerId };

  return undefined;
}

export function buildNotificationLink(input: {
  link?: any;
  view?: any;
  targetId?: any;
  subTargetId?: any;
  entity?: any;
  type?: any;
}): NotificationLink | undefined {
  return (
    normalizeLinkMaybe(input?.link) ||
    normalizeLinkMaybe({
      view: input?.view,
      targetId: input?.targetId,
      subTargetId: input?.subTargetId,
    }) ||
    inferLinkFromEntityOrType({
      type: input?.type,
      entity: input?.entity,
      targetId: input?.targetId,
      subTargetId: input?.subTargetId,
    })
  );
}

function normalizeNotification(id: string, data: any): Notification {
  const timestampMs = toMillisMaybe(data?.timestamp);
  const createdAtMs = toMillisMaybe(data?.createdAt);
  const ts = timestampMs ?? createdAtMs ?? 0;

  const title = safeStr(data?.title);
  const body = safeStr(data?.body);
  const preview = safeStr(data?.preview);
  const message = safeStr(data?.message) ?? body ?? preview ?? title ?? '';

  const link =
    normalizeLinkMaybe(data?.link) ||
    normalizeLinkMaybe({
      view: data?.view ?? data?.screen,
      targetId:
        data?.targetId ??
        data?.id ??
        data?.postId ??
        data?.txId ??
        data?.itemId ??
        data?.licenseId ??
        data?.tournamentId,
      subTargetId: data?.subTargetId ?? data?.commentId ?? data?.offerId,
    }) ||
    inferLinkFromEntityOrType(data);

  return {
    id,
    type: (data?.type ?? NotificationType.ADMIN_ANNOUNCEMENT) as any,
    title,
    body,
    message,
    link,
    actor: data?.actor ?? undefined,
    entity: data?.entity ?? undefined,
    read: Boolean(data?.read),
    timestamp: ts,
  } as any;
}

export function subscribeToNotifications(
  uid: string,
  onChange: (notifs: Notification[]) => void,
  onError?: (err: any) => void,
  opts?: { max?: number },
) {
  const max = Math.max(1, Math.min(Number(opts?.max ?? 50), 100));

  // Production lock: order by the same stable numeric field the UI renders with.
  const q = query(notifCol(uid), orderBy('timestamp', 'desc'), limit(max));

  return onSnapshot(
    q,
    (snap) => {
      const list = snap.docs.map((d) => normalizeNotification(d.id, d.data()));
      list.sort((a, b) => (Number((b as any).timestamp) || 0) - (Number((a as any).timestamp) || 0));
      onChange(list);
    },
    (err) => {
      console.error('[notifications] subscribe error:', err);
      onError?.(err);
      onChange([]);
    },
  );
}

export async function createNotification(input: {
  userId: string;
  id?: string;
  type: any;
  title?: string;
  body?: string;
  preview?: string;
  message?: string;
  link?: any;
  view?: any;
  targetId?: any;
  subTargetId?: any;
  actor?: any;
  actorName?: string;
  entity?: any;
  timestamp?: number;
  read?: boolean;
  [key: string]: any;
}) {
  const { userId, id, timestamp: _ignoredTimestamp, ...rest } = input;

  if (!(await shouldCreateNotificationForRecipient(userId, rest.type))) {
    return id || '';
  }

  const explicitLink = buildNotificationLink({
    link: rest.link,
    view: rest.view,
    targetId: rest.targetId,
    subTargetId: rest.subTargetId,
    entity: rest.entity,
    type: rest.type,
  });

  const isRead = Boolean(input.read ?? false);
  const now = Date.now();

  const payload = stripUndefinedDeep({
    ...rest,
    ...(explicitLink ? { link: explicitLink } : {}),
    read: isRead,
    timestamp: now,
    createdAt: serverTimestamp(),
  });

  if (id) {
    const existingRef = doc(db, 'users', userId, 'notifications', id);
    const isSelfRecipient = auth.currentUser?.uid === userId;
    let existedUnread = false;

    // Reading another user's notification doc is intentionally blocked by rules.
    // For cross-user creates, write the doc directly and let the recipient listener
    // derive unread state from the subcollection snapshot.
    if (isSelfRecipient) {
      const existingSnap = await getDoc(existingRef);
      existedUnread = existingSnap.exists() && existingSnap.data()?.read === false;
    }

    await setDoc(existingRef, payload, { merge: true });

    if (isSelfRecipient && !isRead && !existedUnread) {
      await updateDoc(userRef(userId), {
        unreadNotificationCount: increment(1),
      }).catch(() => {});
    }

    return id;
  }

  const newRef = await addDoc(notifCol(userId), payload);

  // The live notification listener is the source of truth for badges.
  // Keep the legacy unreadNotificationCount mirror only for self-owned writes so
  // cross-user notifications never create hidden permission noise.
  if (auth.currentUser?.uid === userId && !isRead) {
    await updateDoc(userRef(userId), {
      unreadNotificationCount: increment(1),
    }).catch(() => {});
  }

  return newRef.id;
}

export async function markNotificationRead(uid: string, notificationId: string) {
  const ref = doc(db, 'users', uid, 'notifications', notificationId);
  const snap = await getDoc(ref);

  if (!snap.exists()) return;
  if (snap.data()?.read === true) return;

  await updateDoc(ref, { read: true });

  await updateDoc(userRef(uid), {
    unreadNotificationCount: increment(-1),
  }).catch(() => {});
}

export async function markCrewMessageNotificationsReadForThread(uid: string, dmId: string) {
  const currentUid = String(uid || '').trim();
  const threadId = String(dmId || '').trim();
  if (!currentUid || !threadId || auth.currentUser?.uid !== currentUid) return;

  const q = query(notifCol(currentUid), where('type', '==', 'CREW_MESSAGE'), limit(100));
  const snap = await getDocs(q);
  const matching = snap.docs.filter((row) => {
    const data: any = row.data();
    const linkedDm = String(
      data?.targetId || data?.link?.targetId || data?.entity?.dmId || '',
    ).trim();
    return linkedDm === threadId && data?.read !== true;
  });

  if (!matching.length) return;

  const batch = writeBatch(db);
  matching.forEach((row) => batch.update(row.ref, { read: true }));
  await batch.commit();

  await updateDoc(userRef(currentUid), {
    unreadNotificationCount: increment(-matching.length),
  }).catch(() => {});
}

export async function markAllNotificationsReadForUser(uid: string) {
  const q = query(notifCol(uid), where('read', '==', false), limit(200));
  const snap = await getDocs(q);

  if (snap.empty) return;

  const unreadCount = snap.size;

  const batch = writeBatch(db);
  snap.docs.forEach((d) => batch.update(d.ref, { read: true }));
  batch.update(userRef(uid), { unreadNotificationCount: increment(-unreadCount) });
  await batch.commit();
}

export async function clearAllNotificationsForUser(uid: string) {
  const q = query(notifCol(uid), limit(200));
  const snap = await getDocs(q);

  if (snap.empty) return;

  const unreadCount = snap.docs.reduce((sum, d) => sum + (d.data()?.read ? 0 : 1), 0);

  const batch = writeBatch(db);
  snap.docs.forEach((d) => batch.delete(d.ref));

  if (unreadCount > 0) {
    batch.update(userRef(uid), {
      unreadNotificationCount: increment(-unreadCount),
    });
  }

  await batch.commit();
}

export async function markNotificationUnread(uid: string, notificationId: string) {
  const ref = doc(db, 'users', uid, 'notifications', notificationId);
  const snap = await getDoc(ref);

  if (!snap.exists()) return;
  if (snap.data()?.read === false) return;

  await updateDoc(ref, { read: false });

  await updateDoc(userRef(uid), {
    unreadNotificationCount: increment(1),
  }).catch(() => {});
}

export async function recalcUnreadNotificationCount(uid: string) {
  const q = query(notifCol(uid), where('read', '==', false), limit(500));
  const snap = await getDocs(q);

  await updateDoc(userRef(uid), {
    unreadNotificationCount: snap.size,
  }).catch(() => {});
}
